/**
 * Firebase Cloud Messaging (FCM) Client Library
 *
 * Handles push notification setup on the client side:
 * - Service worker registration
 * - FCM token management
 * - Permission requests
 */

"use client";

import {
  getMessaging,
  getToken,
  Messaging,
  onMessage,
} from "firebase/messaging";
import { app } from "./firebase";

let messagingInstance: Messaging | null = null;

/**
 * Detect if running on iOS
 */
export function isIOS(): boolean {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return false;
  }
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
}

/**
 * Detect if the app is running as an installed PWA
 */
export function isInstalledPWA(): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    (window.navigator as Navigator & { standalone?: boolean }).standalone ===
      true
  );
}

/**
 * Check if running in a secure context (HTTPS or localhost)
 */
export function isSecureContext(): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  return window.isSecureContext === true;
}

/**
 * Get detailed push notification support status
 */
export type PushSupportStatus =
  | "supported"
  | "unsupported"
  | "ios-needs-pwa"
  | "insecure-context"
  | "no-service-worker"
  | "no-push-manager"
  | "no-notification-api";

export function getPushSupportStatus(): PushSupportStatus {
  if (typeof window === "undefined") {
    return "unsupported";
  }

  // Check secure context first (HTTPS required)
  if (!isSecureContext()) {
    return "insecure-context";
  }

  // Check for Service Worker support
  if (!("serviceWorker" in navigator)) {
    return "no-service-worker";
  }

  // Check for PushManager support
  if (!("PushManager" in window)) {
    return "no-push-manager";
  }

  // Check for Notification API
  if (!("Notification" in window)) {
    return "no-notification-api";
  }

  // iOS-specific check: must be installed as PWA
  if (isIOS() && !isInstalledPWA()) {
    return "ios-needs-pwa";
  }

  return "supported";
}

/**
 * Get FCM messaging instance
 * Only works in browser environment with service worker support
 */
export function getMessagingInstance(): Messaging | null {
  if (typeof window === "undefined") {
    return null;
  }

  if (!("serviceWorker" in navigator)) {
    console.warn("Service workers are not supported in this browser");
    return null;
  }

  if (!messagingInstance) {
    try {
      messagingInstance = getMessaging(app);
    } catch (error) {
      console.error("Error initializing FCM:", error);
      return null;
    }
  }

  return messagingInstance;
}

/**
 * Register the FCM service worker and wait for it to be active.
 *
 * @throws if service workers are unsupported, registration fails, or the
 *   worker doesn't activate (e.g. times out / becomes redundant). The error
 *   message describes the specific failure so callers can surface it for
 *   diagnostics instead of collapsing every cause into a bare null.
 */
export async function registerServiceWorker(): Promise<ServiceWorkerRegistration> {
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) {
    throw new Error("Service workers are not supported in this environment");
  }

  const registration = await navigator.serviceWorker.register(
    "/firebase-messaging-sw.js",
    { scope: "/" },
  );

  // Helper to wait for service worker activation with timeout
  const waitForActivation = (
    sw: ServiceWorker,
    timeoutMs: number = 10000,
  ): Promise<void> => {
    return new Promise((resolve, reject) => {
      // If already activated, resolve immediately
      if (sw.state === "activated") {
        resolve();
        return;
      }

      const timeout = setTimeout(() => {
        reject(new Error("Service worker activation timed out"));
      }, timeoutMs);

      const handleStateChange = () => {
        if (sw.state === "activated") {
          clearTimeout(timeout);
          sw.removeEventListener("statechange", handleStateChange);
          resolve();
        } else if (sw.state === "redundant") {
          clearTimeout(timeout);
          sw.removeEventListener("statechange", handleStateChange);
          reject(new Error("Service worker became redundant"));
        }
      };

      sw.addEventListener("statechange", handleStateChange);
    });
  };

  // Wait for the service worker to be ready/active
  if (registration.installing) {
    await waitForActivation(registration.installing);
  } else if (registration.waiting) {
    await waitForActivation(registration.waiting);
  }

  // Ensure we have an active worker
  await navigator.serviceWorker.ready;

  return registration;
}

/**
 * Check if push notifications are supported
 */
export function isPushSupported(): boolean {
  return getPushSupportStatus() === "supported";
}

/**
 * Get current notification permission status
 */
export function getNotificationPermission():
  | NotificationPermission
  | "unsupported" {
  if (typeof window === "undefined" || !("Notification" in window)) {
    return "unsupported";
  }
  return Notification.permission;
}

/**
 * Request notification permission from user
 */
export async function requestNotificationPermission(): Promise<
  NotificationPermission | "unsupported"
> {
  if (typeof window === "undefined" || !("Notification" in window)) {
    return "unsupported";
  }

  try {
    const permission = await Notification.requestPermission();
    return permission;
  } catch (error) {
    console.error("Error requesting notification permission:", error);
    return "denied";
  }
}

/**
 * Get FCM token for this device. Requires notification permission to be granted.
 *
 * @param vapidKey - VAPID key from Firebase Console
 * @throws an Error describing the specific failure (messaging unavailable,
 *   permission not granted, service-worker registration failure, or the
 *   underlying getToken()/push-service error). Callers are expected to catch
 *   this; surfacing the real reason is what makes "FCM token null" reports
 *   diagnosable instead of opaque.
 */
export async function getFCMToken(vapidKey: string): Promise<string> {
  const messaging = getMessagingInstance();
  if (!messaging) {
    throw new Error("FCM messaging instance unavailable");
  }

  // Check permission first
  const permission = getNotificationPermission();
  if (permission !== "granted") {
    throw new Error(`Notification permission not granted (state: ${permission})`);
  }

  // Register service worker first (throws with a descriptive reason on failure)
  const registration = await registerServiceWorker();

  // Get the token. getToken() rejects (rather than returning empty) for most
  // real-world failures — VAPID mismatch, push-service errors, etc. — so wrap
  // it to attach context while preserving the original message.
  let token: string;
  try {
    token = await getToken(messaging, {
      vapidKey,
      serviceWorkerRegistration: registration,
    });
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    throw new Error(`getToken() failed: ${message}`);
  }

  if (!token) {
    throw new Error("getToken() returned an empty token");
  }

  return token;
}

/**
 * Set up foreground message handler
 * Called when a message is received while the app is in focus
 *
 * @param callback - Function to call when a message is received
 */
export function onForegroundMessage(
  callback: (payload: {
    notification?: { title?: string; body?: string };
    data?: Record<string, string>;
  }) => void,
): (() => void) | null {
  const messaging = getMessagingInstance();
  if (!messaging) {
    return null;
  }

  return onMessage(messaging, (payload) => {
    callback(payload);
  });
}

/**
 * Delete the current FCM token (for logout or unsubscribe)
 */
export async function deleteFCMToken(): Promise<boolean> {
  const messaging = getMessagingInstance();
  if (!messaging) {
    return false;
  }

  try {
    const { deleteToken } = await import("firebase/messaging");
    await deleteToken(messaging);
    return true;
  } catch (error) {
    console.error("Error deleting FCM token:", error);
    return false;
  }
}
